import { ReactNode, RefObject } from 'react';

import { DragDropTexts as FileUploadDragDropTexts } from './hooks/useDragDropWrapper';

export type ChatWindowInputFileError = ReactNode | null;

export enum MessageType {
  INFO = 'info',
  SAVE = 'save',
  PROCESS = 'process',
  MODEL_CHANGE = 'model_change',
  CONCEPT_GROUP_REVIEW = 'concept_group_review',
  OMOP_MAPPING_RESULTS = 'omop_mapping_results',
  ANALYTICAL_VARIABLE = 'analytical_variable',
  PROGRESS = 'progress',
}

export type FeedbackRating = 'thumbs_up' | 'thumbs_down';

export interface MessageFeedback {
  rating: FeedbackRating;
  comment?: string;
  updated_at: string;
}

export interface Message {
  message: string | React.ReactNode;
  currentUser: string;
  isCurrentUser: boolean;
  timestamp?: string;
  type?: MessageType;
  action?: React.ReactNode;
  userInitials?: string;
  // Legacy messages created before feedback support may not include messageId
  messageId?: string;
  feedback?: MessageFeedback | null;
}

export type AVProgressStatus =
  | 'pending'
  | 'in_progress'
  | 'searching'
  | 'completed'
  | 'failed'
  | 'retrying';

export type AVProgressAction = 'add' | 'modify' | 'remove' | 'study_period';

export interface AVProgressItem {
  avIndex: number;
  name: string;
  action: AVProgressAction;
  status: AVProgressStatus;
  retryMessage?: string;
}

// Define ProgressState locally to avoid circular dependency
export interface ProgressState {
  currentStep: {
    step: string;
    message?: string; // Optional - backend can provide custom message (e.g., "Second try - ...")
  } | null;
  avProgress?: AVProgressItem[] | null;
}

export interface SuggestedAction {
  id: string;
  label: string;
  onClick: () => void;
}

export interface ChatWindowFileUploadConfig {
  accept?: string;
  selectedFile?: File | null;
  onFileSelected: (file: File) => void;
  isDisabled?: boolean;
  buttonLabel?: string;
  buttonAriaLabel?: string;
  selectedFileName?: string;
  onClearSelectedFile?: () => void;
  maxFileSizeMB?: number;
}

export interface ChatWindowProps {
  // Layout & Styling
  width?: number;
  height?: string;
  borderRadius?: string;
  border?: string;

  // Header
  title: string;
  headerRightContent?: ReactNode;

  // Messages
  messages: Message[];
  isWaitingForResponse: boolean;
  isReady: boolean;
  progressState?: ProgressState;

  // Input
  userPrompt: string;
  onUserPromptChange: (value: string) => void;
  onSendMessage: () => void;
  isOwner: boolean;
  userInitials?: string;

  /** Override the default input placeholder (e.g. to explain why input is disabled). */
  inputPlaceholder?: string;

  // Features
  suggestedActions?: SuggestedAction[];
  disclaimerText: string;
  progressMessageMap?: Record<string, string>;
  /** 'fixed' renders progress between messages and input (default). 'inline' renders progress as part of the message flow. */
  progressPlacement?: 'fixed' | 'inline';
  fileUploadConfig?: ChatWindowFileUploadConfig;
  fileUploadDragDropTexts?: FileUploadDragDropTexts;

  // Refs (for scroll management)
  chatWindowRef: RefObject<HTMLDivElement>;
  messagesEndRef: RefObject<HTMLDivElement>;
}
